import {
  ButtonText,
  Checkbox,
  Select,
  Table
} from '@morressier/alembic';
import { memo, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormValues } from './AssignMembers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { Tooltip } from '@chakra-ui/tooltip';

interface Props {
  member: any;
  roles: string[];
  submissionContextId: string;
  isInvalid?: boolean;
  isCheckboxHidden?: boolean;
}

export default memo(function MemberRow({
  member,
  roles,
  submissionContextId,
  isCheckboxHidden = false
}: Props) {
  const {
    control,
    formState: { errors },
    setValue,
    clearErrors
  } = useFormContext<FormValues>();

  const assignment = useWatch({
    control,
    name: `assignments.${member.id}`
  });

  const selected = assignment?.selected ?? false;

  const roleOptions = useMemo(
    () => roles.map(role => <option key={role}>{role}</option>),
    [roles]
  );

  const error = errors?.assignments?.[member.id]?.roleName;

  const handleSelectMember = (isChecked: boolean) => {
    setValue(
      `assignments.${member.id}`,
      {
        ...member,
        workspaceMembershipId: member.id,
        submissionContextId,
        roleName: assignment?.roleName ?? '',
        selected: isChecked
      },
      {
        shouldValidate: true,
        shouldDirty: true
      }
    );
    if (!isChecked) {
      clearErrors(`assignments.${member.id}`);
    }
  };

  return (
    <Table.Row gap={2}>
      {!isCheckboxHidden && (
        <Table.RowCell width="40px">
          <Checkbox
            name={`member-${member.id}`}
            id={`member-${member.id}`}
            ariaLabel="Select member"
            isChecked={selected}
            onChange={e => handleSelectMember(e.target.checked)}
          >
            {''}
          </Checkbox>
        </Table.RowCell>
      )}

      <Table.RowCell flex="1">
        {member.user?.firstName} {member.user?.lastName}
      </Table.RowCell>

      <Table.RowCell flex="1">{member.user?.email}</Table.RowCell>

      <Table.RowCell width="180px">
        <Select
          width="100%"
          disabled={!selected}
          value={assignment?.roleName ?? ''}
          isInvalid={!!error}
          onChange={e => {
            const value = e.target.value;
            setValue(`assignments.${member.id}`, {
              ...assignment,
              roleName: value
            });
            if (value) {
              clearErrors(`assignments.${member.id}.roleName`);
            }
          }}
        >
          <option value="">Select</option>
          {roleOptions}
        </Select>
      </Table.RowCell>
      {isCheckboxHidden && (
        <Table.RowCell width="40px">
          <ButtonText
            aria-label="Remove assigned member"
            variant="caution"
            data-cy="delete-icon"
            ml={1}
            onClick={() => handleSelectMember(false)}
          >
            <Tooltip label="Remove assigned member" hasArrow placement="right">
              <FontAwesomeIcon icon={faTrashCan} />
            </Tooltip>
          </ButtonText>
        </Table.RowCell>
      )}
    </Table.Row>
  );
});
